import * as React from 'react';

import { showErrorToast, showSuccessToast } from '../../../shared/ToastUtils';

import { ApiRoutes } from '../../../utilities/ApiRoutes';
import { DocumentMeta } from './DocumentMeta';
import { FileUploader } from './FileUploader';
import { IDocumentModel } from '../../../interfaces/ICase';
import { IMultiSelectOption } from '../../../interfaces/ILookup';
import { IValidation } from '../../../interfaces/IError';
import { LoadingButton } from '../../../shared/buttons/LoadingButton';
import { Modal } from 'react-bootstrap';
import { useDocumentUpload } from '../../../shared/react-query-hooks/useDocumentUpload';
import { useEffect } from 'react';
import { useZoneContext } from '../../../contexts/useZoneContext';
import { v4 as uuidv4 } from 'uuid';

interface IDocumentUploadModalProps {
    documentTypeId: number;
    documentSubTypeId?: number;
    entityGuid: string;
    entityTypeId: number;
    existingDocuments?: IDocumentModel[];
    noteTypeId?: number;
    strategyTypeId?: number;
    open: boolean;
    onHide: () => void;
    onSaveComplete: (documents: IDocumentModel[]) => void;
}

function DocumentUploadModal(props: IDocumentUploadModalProps) {
    if (!props.open) return null;

    const [documents, setDocuments] = React.useState<IDocumentModel[]>();
    const [documentValidations, setDocumentValidations] = React.useState<
        Record<string, IValidation>
    >({});
    const [validation, setValidation] = React.useState<IValidation>({});
    const { zoneGuid } = useZoneContext();

    const { uploadAsync, isUploading, data, error } = useDocumentUpload<IDocumentModel[]>({
        endpoint: '/' + ApiRoutes.EntityDocumentsUpload,
        onProgress: ({ percent }) => console.log(`Upload progress: ${percent}%`),
    });

    const validateDocuments = () => {
        setDocumentValidations({});

        let hasErrors = false;

        documents?.forEach((document) => {
            const validation: IValidation = {};

            if (!document.file && document.guid) {
                validation.file = ['File is required '];
                hasErrors = true;
            }

            if (!document.fileName && document.guid) {
                validation.filename = ['File Name is required '];
                hasErrors = true;
            }

            if (
                documents
                    .filter((d) => d.guid !== document.guid)
                    .some((d) => d.fileName === document.fileName)
            ) {
                validation.uniqueFilename = ['File Name must be unique per upload'];
                hasErrors = true;
            }

            if (hasErrors && document.guid) {
                setDocumentValidations((prev) => ({ ...prev, [document.guid!]: validation }));
            }
        });

        return !hasErrors;
    };

    const handleUploadDocumentsClick = async () => {
        setValidation({});

        if (!validateDocuments()) {
            return;
        }

        if ((documents?.length ?? 0) === 0) {
            setValidation({ model: ['Select files to upload'] });
            return;
        }

        uploadAsync({
            documents: documents?.filter((doc) => !!doc.file) ?? [],
            entityGuid: props.entityGuid,
            entityTypeId: props.entityTypeId,
            noteTypeId: props.noteTypeId,
            strategyTypeId: props.strategyTypeId,
            tenantGuid: zoneGuid,
        })
            .then((resp) => {
                const docs = resp.data;
                const documentCount = docs?.length ?? 0;
                const noun = documentCount > 1 || documentCount === 0 ? 'documents' : 'document';

                props.onSaveComplete(docs ?? []);
                showSuccessToast(`${docs?.length ?? 0} ${noun} uploaded successfully`);
                handleCancelClick();
            })
            .catch(() => {
                const documentCount = documents?.length ?? 0;
                const noun = documentCount > 1 || documentCount === 0 ? 'documents' : 'document';

                showErrorToast(`Error uploading ${noun}`);
                setValidation(validation);
            });
    };

    const handleCancelClick = () => {
        props.onHide();
    };

    const handleAttachmentChange = (files: FileList) => {
        if (!files || files.length === 0) return;
        setValidation((prev) => ({ ...prev, model: [''] }));

        const documentModels: IDocumentModel[] = Array.from(files).map((file) => ({
            guid:
                props.existingDocuments?.find((ed) => ed.fileName === file.name)?.guid ?? uuidv4(),
            documentTypeId: props.documentTypeId,
            documentSubTypeId: props.documentSubTypeId,
            fileName: file.name,
            file: file,
            title: file.name,
        }));

        const allDocuments = (documents ?? []).concat(documentModels);
        const uniqueDocuments = Array.from(
            new Map(allDocuments.map((doc) => [doc.fileName, doc])).values()
        );

        setDocuments(uniqueDocuments);
    };

    const handleDocumentCategoriesChange = (
        guid: string | undefined,
        selectedOptions: IMultiSelectOption[]
    ) => {
        setDocuments((prev) => {
            const found = prev?.find((doc) => doc.guid === guid);

            if (!found) {
                return prev;
            }

            found.documentCategoryTypeIds = selectedOptions.map((x) => x.id);
            return [...(prev ?? [])];
        });
    };

    const handleFileNameChange = (guid: string | undefined, value: string) => {
        setDocuments((prev) => {
            const found = prev?.find((doc) => doc.guid === guid);

            if (!found) {
                return prev;
            }

            found.fileName = value;
            return [...(prev ?? [])];
        });

        validateDocuments();
    };

    const handleRemoveClick = (guid: string) => {
        setDocuments((prev) => prev?.filter((doc) => doc.guid !== guid));
    };

    const getDocumentNameExists = (current: string) =>
        !!props.existingDocuments?.some((ed) => ed.fileName === current);

    useEffect(() => {
        setDocumentValidations({});
    }, []);

    const documentCount = documents?.length ?? 0;
    const uploadButtonText =
        documentCount > 1 ? 'Upload ' + documentCount + ' Documents' : 'Upload Document';

    return (
        <Modal centered show={props.open} onHide={props.onHide} size="lg" backdrop>
            <Modal.Header>
                <Modal.Title>Upload Documents</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {validation.model && <div className="text-danger mb-2">{validation.model}</div>}
                <div className="mb-2">
                    <FileUploader
                        disabled={isUploading}
                        onFilesSelected={(files) => {
                            handleAttachmentChange(files);
                        }}
                        multiple
                    />
                </div>
                <ul>
                    {documents?.map((document) => (
                        <DocumentMeta
                            key={document.guid}
                            disabled={isUploading}
                            document={document}
                            getDocumentNameExists={getDocumentNameExists}
                            entityTypeId={props.entityTypeId}
                            onCategoriesChange={handleDocumentCategoriesChange}
                            onRemoveClick={handleRemoveClick}
                            onFileNameChange={handleFileNameChange}
                            validation={documentValidations[document.guid!]}
                        />
                    ))}
                </ul>
            </Modal.Body>
            <Modal.Footer>
                <LoadingButton
                    loading={isUploading}
                    loadingText="Uploading..."
                    onClick={handleUploadDocumentsClick}
                    text={uploadButtonText}
                    variant="primary"
                    icon="fal fa-upload"
                    disabled={documentCount === 0}
                />
                <button
                    className="btn btn-default"
                    onClick={handleCancelClick}
                    disabled={isUploading}
                >
                    Cancel
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default DocumentUploadModal;
